<template>
  <div>
    <VForm @submit="login">
      <div>
        <!-- Email / Username -->
        <div :class="hasError ? 'has-error' : ''">
          <SInput
            v-cloak
            v-model="username"
            validation-field-name="username"
            validation-rules="required"
            :label-text="$t('login.login_sign_in.input_username.placeholder')"
            name="username"
            size="lg"
            data-cy="login-username"
            data-test="username-input"
          />
        </div>

        <!--Do not add autocomplete as it causes problems with the TFA. The users who use TFA can no longer log in if autocomplete is added-->
        <!-- Password -->
        <div class="mt-2" :class="hasError ? 'has-error' : ''">
          <SInput
            v-cloak
            v-model="password"
            validation-field-name="password"
            validation-rules="required"
            :label-text="$t('login.login_sign_in.input_password.placeholder')"
            name="password"
            type="password"
            size="lg"
            data-cy="login-password"
            data-test="password-input"
          >
            <template #right-action>
              <b-button
                v-if="brandingConfig.login.form.inline_reset_password"
                id="reset-password-btn"
                size="md"
                class="primary position-absolute py-2 px-0 secondary-bg align-content-center"
                variant="transparent"
                data-test="reset-password"
                @click="$router.push({ name: 'login-forgot-data' })"
              >
                <span class="sr-only">{{ $t('login.login_sign_in.input_password.placeholder') }} </span>{{ $t('login.login_sign_in.forgot_short') }}
              </b-button>
            </template>
          </SInput>
        </div>

        <!-- Login button -->
        <b-button
          id="login-submit"
          v-btn-focus-restore-after-disable
          type="submit"
          size="lg"
          block
          class="mt-2 mb-2"
          :variant="singleSignOnActivated ? 'outline-primary' : 'primary'"
          data-test="submit-button"
          data-cy="login-submit"
          :disabled="$wait.waiting('waiting for login')"
        >
          {{ $t('global.login') }}
        </b-button>

        <!-- Forgot password button -->
        <b-row
          v-if="!hideForgotPassword"
          class="problems fz-base mt-2"
          data-test="forgot-password-row"
        >
          <b-col class="text-center">
            <b-button variant="link"
                      block
                      class="p-3 font-weight-bold"
                      @click="$router.push({ name: 'login-forgot-data' })"
            >
              {{ $t('login.login_sign_in.forgot_access_data') }}
            </b-button>
          </b-col>
        </b-row>
      </div>
    </VForm>

    <SModal
      v-model="showInfoModal"
      :header-title="$t('login.login_sign_in.error_info_modal.title')"
      :show-footer-gradient="false"
      return-focus="#login-submit"
    >
      <login-error-info v-if="showInfoModal"
                        @close="showInfoModal = false"
                        @open-explanatory-modal="showGeneralInfo"
      />
    </SModal>

    <SModal
      v-model="showGeneralInfoModal"
      :header-title="$t('login.login_sign_in.general_info_modal.title')"
      hide-footer
      :show-footer-gradient="false"
      return-focus="#login-submit"
    >
      <login-general-info v-if="showGeneralInfoModal" @close="showGeneralInfoModal = false" />
    </SModal>

    <SModal
      v-model="showLoginModal"
      centered
      :header-title="$t('login.login_sign_in.verification_modal.title')"
      :show-footer-gradient="false"
      return-focus="#login-submit"
      @hide="resetCredentials"
      @shown="useBackupCredentials"
    >
      <VForm v-if="showLoginModal"
             id="form-2fa-modal"
             ref="form-2fa"
             @submit="login"
      >
        <div class="text-center mb-5">
          <img class="primary-svg-filter"
               src="/img/miscellaneous/security/two-factor-auth-warning.svg"
               alt=""
               loading="lazy"
          />
        </div>

        <div class="text-center mb-4">
          {{ $t(`login.login_sign_in.verification_modal.description.${tfa}`) }}
          <SLink class="font-weight-bold"
                 :href="$t('login.login_sign_in.verification_modal.description.link.href')"
          >{{ $t('login.login_sign_in.verification_modal.description.link.text') }}
          </SLink>
        </div>

        <div class="text-center mb-5">
          <b-button variant="transparent"
                    class="primary font-weight-bold"
                    @click="resetCode"
          >
            {{ $t('login.login_sign_in.verification_modal.link') }}
          </b-button>
        </div>

        <SInput
          id="code-input"
          ref="tokenInput"
          v-model="code"
          validation-field-name="code"
          validation-field-label="value"
          validation-rules="min_value:10000|max_value:99999|required"
          :label-text="$t('login.login_sign_in.verification_modal.input.placeholder')"
          hide-label
          type="number"
          min="10000"
          max="99999"
          size="lg"
          autocomplete="off"
          :placeholder="$t('login.login_sign_in.verification_modal.input.placeholder')"
        />
      </VForm>
      <SModalPortalTo>
        <b-button
          v-btn-focus-restore-after-disable
          form="form-2fa-modal"
          size="lg"
          variant="primary"
          block
          type="submit"
          :disabled="$wait.waiting('waiting for login')"
        >
          {{ $t('login.login_sign_in.verification_modal.submit') }}
        </b-button>
      </SModalPortalTo>
    </SModal>
  </div>
</template>

<script>
import { waitFor } from 'src/config/setup/plugins/waitFor/index.js'
import LoginGeneralInfo from 'src/views/Authentification/Login/LoginGeneralInfo.vue'
import LoginErrorInfo from 'src/views/Authentification/Login/LoginErrorInfo.vue'
import brandingMixin from 'src/mixins/branding/brandingMixin.js'
import SModalPortalTo from 'src/components/base/SModal/SModalPortalTo.vue'
import clyTracking from 'src/config/services/countlyTracking/clyTracking.js'

export default {
  name: 'LoginForm',
  components: {
    SModalPortalTo,
    LoginGeneralInfo,
    LoginErrorInfo,
  },

  mixins: [brandingMixin],
  props: {
    singleSignOnActivated: {
      type: Boolean,
      default: false,
    },

    hideForgotPassword: {
      type: Boolean,
      default: false,
    },

  },

  data: () => ({
    username: '',
    backupUsername: '',
    password: '',
    backupPassword: '',
    code: '',
    tfa: null,
    showInfoModal: false,
    showGeneralInfoModal: false,
    showLoginModal: false,
    apiCallCounts: 0,
    hasError: false,
  }),

  computed: {
    userMustSeeInfoModalOnNextTry() {
      return (
        this.brandingConfig.login.registration_code &&
        ((this.apiCallCounts === 2 && this.password.length === 12) ||
          (this.apiCallCounts === 3 && this.password.length !== 12) ||
          this.apiCallCounts === 6)
      )
    },
  },

  methods: {
    login: waitFor('waiting for login', async function () {
      try {
        const res = await this.$store.dispatch('users/login', {
          identifier: this.backupUsername || this.username,
          password: this.backupPassword || this.password,
          slink: this.$store.state.slink,
          showError: !this.userMustSeeInfoModalOnNextTry,
          token: this.code,
          track: () => clyTracking.trackAuth().action.login({ origin: 'manual' })
        })

        this.code = ''
        if (res.status !== 'SUCCESS') return
        if (res.data.tfa_mode !== 'email') return
        this.tfa = res.data.tfa_mode
        this.useBackupCredentials()
        this.showLoginModal = true
      } catch (e) {
        if (e.response?.status !== 400 && !e.response?.data?.error?.length) {
          this.hasError = false
          return this.$toastNotify({
            type: 'error',
            text: this.$t('ui.connection_lost.check_internet'),
          })
        }

        this.code = ''
        this.showLoginModal && this.$refs['form-2fa'].resetField('code')
        this.showErrorMessage()
      }
    }),



    showErrorMessage() {
      // Display error message if it's first try
      // Display info modal if it's (second try && password length is 12) OR (it's third try)
      if (!this.showLoginModal) this.apiCallCounts += 1
      this.hasError = true
      this.showInfoModal = this.userMustSeeInfoModalOnNextTry
    },

    resetCode() {
      this.code = ''
      this.login()
    },

    resetCredentials() {
      this.code = ''
      this.backupUsername = ''
      this.backupPassword = ''
    },

    useBackupCredentials() {
      /*
       * Lastpass changes identifier in the background while tabbing out & pressing enter
       * we make a backup for each and use this instead
       * */
      this.backupPassword = this.password
      this.backupUsername = this.username
    },

    showGeneralInfo() {
      this.showInfoModal = false
      this.showGeneralInfoModal = true
    },
  },
}
</script>

<style scoped lang="scss">
.has-error {
  .form-control {
    border-color: $color-danger !important;
    color: $color-danger-dark;
    border-width: 2px !important;
  }
}

.icon-info {
  position: relative;
  bottom: 2px;
}

#reset-password-btn {
  top: 50%;
  right: 2px;
  transform: translateY(-50%);
  z-index: 2;
  border: none;
}
</style>
